import React from "react";
import { SIZES } from "../../utils/photos";
import PhotoImage from "./PhotoImage";

/**
 * Mosaico de fotos en filas justificadas, sin huecos.
 *
 * Reemplaza al mosaico por columnas CSS, que reparte las fotos en columnas de
 * alto distinto: con una vertical larga en una columna y dos horizontales en
 * las otras, quedaba un rectángulo negro al final — en la web de un fotógrafo
 * eso se lee como una foto que no cargó.
 *
 * Aquí las fotos se reparten en filas. Dentro de una fila cada foto ocupa un
 * ancho proporcional a su formato, y la fila entera lleva como aspect-ratio la
 * suma de los formatos: así todas las fotos de la fila miden lo mismo de alto,
 * la fila llena el ancho exacto y ninguna foto se recorta. El bloque termina
 * siempre en un borde recto, tenga las fotos que tenga.
 *
 * `OBJETIVO` es cuánto "formato" cabe por fila: 1.3 en móvil es una horizontal
 * o dos verticales, 3.6 en escritorio son dos horizontales y una vertical. Se pintan
 * las dos particiones y se muestra una según el ancho; las imágenes de la que
 * está oculta no se descargan, porque van con loading="lazy".
 */
const OBJETIVO = { movil: 1.3, escritorio: 3.6 };

const formato = (foto) => foto.w / foto.h;
const suma = (fila) => fila.reduce((total, foto) => total + formato(foto), 0);

/** Reparte las fotos en filas cuyo formato sumado ronda el objetivo. */
export const repartirEnFilas = (fotos, objetivo) => {
  const filas = [];
  let fila = [];

  fotos.forEach((foto) => {
    fila.push(foto);
    if (suma(fila) >= objetivo) {
      filas.push(fila);
      fila = [];
    }
  });
  if (fila.length) filas.push(fila);

  /*
    La última fila suele quedar corta: una foto sola a todo el ancho saldría
    gigante al lado de las demás. Se junta con la anterior y se vuelve a partir
    por donde las dos mitades quedan más parejas.
  */
  if (filas.length > 1 && suma(filas[filas.length - 1]) < objetivo * 0.7) {
    const juntas = [...filas[filas.length - 2], ...filas[filas.length - 1]];
    let corte = 1;
    let menorDiferencia = Infinity;
    for (let i = 1; i < juntas.length; i += 1) {
      const diferencia = Math.abs(suma(juntas.slice(0, i)) - suma(juntas.slice(i)));
      if (diferencia < menorDiferencia) {
        menorDiferencia = diferencia;
        corte = i;
      }
    }
    filas.splice(filas.length - 2, 2, juntas.slice(0, corte), juntas.slice(corte));
  }

  return filas;
};

const Filas = ({ fotos, objetivo, className }) => (
  <div className={`flex-col gap-2 sm:gap-3 ${className}`}>
    {repartirEnFilas(fotos, objetivo).map((fila) => (
      <div
        key={fila[0].id}
        className="flex gap-2 sm:gap-3"
        style={{ aspectRatio: suma(fila).toFixed(4) }}
      >
        {fila.map((foto) => (
          <figure
            key={foto.id}
            className="group relative h-full min-w-0 overflow-hidden"
            style={{ flex: `${formato(foto).toFixed(4)} 1 0%` }}
          >
            <PhotoImage foto={foto} bn sizes={SIZES.masonry} className="h-full w-full object-cover" />
            {foto.label ? (
              <figcaption className="pointer-events-none absolute inset-0 flex items-end bg-gradient-to-t from-navy-900/80 to-transparent p-4 opacity-0 transition duration-500 group-hover:opacity-100">
                <span className="text-sm text-sand-100">{foto.label}</span>
              </figcaption>
            ) : null}
          </figure>
        ))}
      </div>
    ))}
  </div>
);

const FotoMosaico = ({ fotos, className = "" }) => (
  <div className={className}>
    <Filas fotos={fotos} objetivo={OBJETIVO.movil} className="flex sm:hidden" />
    <Filas fotos={fotos} objetivo={OBJETIVO.escritorio} className="hidden sm:flex" />
  </div>
);

export default FotoMosaico;
